import { SmartCoverRequestEmployee } from 'features/MyWeek/slice';
import { ReasonKey } from 'features/scheduleBuilder/slices/reassignmentRecommendations/types';

import { toI18n } from 'util/i18n';

/**
 * Returns the cover request reason options in the correct order.
 *
 * This utility maintains the intended UX order of options rather than
 * the alphabetical ordering that would result from Object.keys().
 *
 * @returns {Array<{label: string, value: string}>} Array of option objects for the dropdown
 */
export const getCoverRequestReasonOptions = () => {
  const I18N_KEY = 'schedule_builder.smart_covers.reason_for_cover_options';

  const REASON_ORDER = [
    'academic',
    'family',
    'health',
    'personal',
    'schedule',
    'travel',
    'job_conflict',
    'other',
  ] as const;

  return REASON_ORDER.map(key => ({
    label: toI18n(`${I18N_KEY}.${key}`),
    value: key,
  }));
};

/**
 * Adds mock employees to the existing recommended list for testing purposes.
 *
 * @param existingRecommended - The existing array of recommended employees
 * @returns {SmartCoverRequestEmployee[]} The existing list with mock employees appended
 */
export const addMockEmployeesToRecommended = (
  existingRecommended: SmartCoverRequestEmployee[]
): SmartCoverRequestEmployee[] => {
  const mockEmployees: SmartCoverRequestEmployee[] = [
    {
      id: 'mock-1',
      name: 'Sarah Johnson',
      label: 'Sarah Johnson',
      value: 9001,
      firstName: 'Sarah',
      lastName: 'Johnson',
      avatarUrl: 'https://i.pravatar.cc/150?img=1',
      jobId: 101,
      jobUUID: 'mock-job-uuid-1',
      isRecommended: true,
      roleName: 'Barista',
      reasons: [
        { reason_key: 'role_match' as ReasonKey, reason: 'Skilled Barista' },
        {
          reason_key: 'on_time_arrival' as ReasonKey,
          reason: 'Reliably on-time',
        },
      ],
    },
    {
      id: 'mock-2',
      name: 'Mike Chen',
      label: 'Mike Chen',
      value: 9002,
      firstName: 'Mike',
      lastName: 'Chen',
      avatarUrl: 'https://i.pravatar.cc/150?img=2',
      jobId: 102,
      jobUUID: 'mock-job-uuid-2',
      isRecommended: true,
      roleName: 'Server',
      reasons: [
        { reason_key: 'seniority' as ReasonKey, reason: 'Equal tenure' },
        { reason_key: 'role_match' as ReasonKey, reason: 'Skilled Server' },
      ],
    },
    {
      id: 'mock-3',
      name: 'Emily Davis',
      label: 'Emily Davis',
      value: 9003,
      firstName: 'Emily',
      lastName: 'Davis',
      avatarUrl: 'https://i.pravatar.cc/150?img=3',
      jobId: 103,
      jobUUID: 'mock-job-uuid-3',
      isRecommended: true,
      roleName: 'Shift Lead',
      reasons: [
        { reason_key: 'role_match' as ReasonKey, reason: 'Skilled Shift Lead' },
        { reason_key: 'seniority' as ReasonKey, reason: 'Equal tenure' },
        {
          reason_key: 'on_time_arrival' as ReasonKey,
          reason: 'Reliably on-time',
        },
      ],
    },
    {
      id: 'mock-4',
      name: 'Alex Rodriguez',
      label: 'Alex Rodriguez',
      value: 9004,
      firstName: 'Alex',
      lastName: 'Rodriguez',
      avatarUrl: 'https://i.pravatar.cc/150?img=4',
      jobId: 104,
      jobUUID: 'mock-job-uuid-4',
      isRecommended: true,
      roleName: 'Cashier',
      reasons: [
        {
          reason_key: 'overtime' as ReasonKey,
          reason: 'Creates 2hrs overtime',
        },
      ],
    },
    {
      id: 'mock-5',
      name: 'Jordan Kim',
      label: 'Jordan Kim',
      value: 9005,
      firstName: 'Jordan',
      lastName: 'Kim',
      avatarUrl: 'https://i.pravatar.cc/150?img=5',
      jobId: 105,
      jobUUID: 'mock-job-uuid-5',
      isRecommended: true,
      roleName: 'Kitchen Staff',
      reasons: [
        {
          reason_key: 'role_match' as ReasonKey,
          reason: 'Skilled Kitchen Staff',
        },
      ],
    },
    {
      id: 'mock-6',
      name: 'Taylor Brown',
      label: 'Taylor Brown',
      value: 9006,
      firstName: 'Taylor',
      lastName: 'Brown',
      avatarUrl: 'https://i.pravatar.cc/150?img=6',
      jobId: 106,
      jobUUID: 'mock-job-uuid-6',
      isRecommended: true,
      roleName: 'Host',
      reasons: [
        { reason_key: 'seniority' as ReasonKey, reason: 'Equal tenure' },
        {
          reason_key: 'conflicting_shift' as ReasonKey,
          reason: 'Schedule conflicts',
        },
      ],
    },
  ];

  return [...existingRecommended, ...mockEmployees];
};
